<script setup lang="ts">
import { computed, onMounted, Ref, ref } from 'vue';
import i18n from '@/localization/i18n';

import BusinessRequestProgressBar from "./BusinessRequestProgressBar.vue";
import CustomInput from '@/views/components/CustomInput.vue';
import MessagesVue from '@/views/components/Messages.vue';
import {storeToRefs} from "pinia";
import {useBusinessRequestFormStore} from "@/pages/index/stores/business-request-form-store.ts";
import { useFormAlertsStore } from '@/stores/alerts';
import { schemaValidatorLogin} from "@/pages/index/validation/businessRequestLoginSchema"
import VueTurnstile from 'vue-turnstile'

const businessRequestFormStore = useBusinessRequestFormStore()
const {signUpFormData, loginFormData, loginFormErrors, login_loading, forgotPasswordFormData} = storeToRefs(businessRequestFormStore)

const t = i18n.global.t
const loginAlerts = useFormAlertsStore()

const turnstile_site_key = import.meta.env.VITE_TURNSTILE_SITE_KEY
const isMobile: Ref<boolean> = ref(false)
const mobileMaxWIdth = 1300

const handleResize = () => {
    isMobile.value = window.innerWidth <= mobileMaxWIdth
};

onMounted(() => {
    loginFormData.value.email = forgotPasswordFormData.value.email != '' ? forgotPasswordFormData.value.email : signUpFormData.value.email
    loginFormData.value.password = ''
    loginFormData.value.cloud_flare_token = ''

    handleResize()
    window.addEventListener('resize', handleResize)
})

const forgotPassword = () => {
    forgotPasswordFormData.value.email = loginFormData.value.email
    const dismissRequestPostModal = document.getElementById("dismissRequestPostModal")
    dismissRequestPostModal?.click()

    const reinitializePasswordButton = document.getElementById("reinitializePasswordButton")
    reinitializePasswordButton?.click()
}

const shouldEnableSubmitButton = computed(() => {
  try {
        schemaValidatorLogin(false).validateSync(loginFormData.value)
        return true
  } catch (error) {
    return false
  }
})
</script>

<template>
    <div class="rounded main fullHeight">
        <BusinessRequestProgressBar />
        <div class="fullRemainingHeight">
            <div class="expanded">
                <div class="h-style h3-style use-poppins text-center mt-2">
                    {{ t("w_txt_business_request_login_heading") }}
                </div>
                <div class="mt-3 mx-md-5">
                    <p class="p-style">
                    {{ t("w_txt_business_request_login_paragraph") }}
                </p>
                </div>
                <div class="row">
                    <form @submit.prevent class="col-12 col-sm-12 mb-2 border-end-light">
                        <template v-if="loginAlerts.getSize > 0">
                            <template v-for="alert in loginAlerts.getAlerts">
                                <MessagesVue v-if="alert.scope === 'login'" />
                            </template>
                        </template>
                        <div class="row">
                            <div class="col-8 mb-2 custom-input-label">
                                <CustomInput
                                    type="email"
                                    maxlength="254"
                                    id="new_email"
                                    v-model:value="loginFormData.email"
                                    :errors="loginFormErrors.email"
                                    :input-label="t('w_frm_business_request_login_email_pl')"
                                        />
                            </div>
                            <div class="col-4 mb-2 row d-flex align-items-center" style="height: 4rem !important">
                              <a href="#" @click="() => {businessRequestFormStore.next_step('signup', false)}" class="signUpLink mt-4 text-start px-0" id="loginLink">{{ t('w_txt_business_request_login_no_account') }}</a>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-8 mb-2 custom-input-label">
                                <CustomInput
                                    type="password"
                                    id="login_password"
                                    autocomplete="current-password"
                                    v-model:value="loginFormData.password"
                                    :errors="loginFormErrors.password"
                                    :placeholder="t('w_frm_plPassword')"
                                    :input-label="t('w_frm_business_request_login_password_pl')"
                                />
                            </div>
                        </div>
                        <div class="row">
                          <div class="col-12">
                            <div class="d-flex align-items-center mt-1">
                              <p>{{ t('w_txt_csLogin_reinitializePasswordPrompt') }} <a @click="forgotPassword" href="#" class="signUpLink" >{{ t('w_act_csLogin_reinitializePassword') }}
                              </a>
                              </p>
                            </div>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-12 mt-1 mb-2 text-center">
                            <div class="captcha-wrapper"
                              :style="{
                                transform: isMobile ? 'scale(1.17)' : 'scale(1.38)',
                                transformOrigin: 'top center',
                                marginTop: '10px',
                                marginBottom: '10px'
                              }">
                              <vue-turnstile
                                :site-key="turnstile_site_key"
                                v-model="loginFormData.cloud_flare_token" />
                            </div>
                            <p class="text-danger mt-0 mt-md-2" v-if="loginFormErrors.cloud_flare_token">{{
                              loginFormErrors.cloud_flare_token }}</p>
                          </div>
                        </div>
                    </form>
                </div>
            </div>
            <div style="margin-top: auto; margin-bottom:10px ">
                <button @click="businessRequestFormStore.prev_step('keywords')" type="button" class="btn btn-outline-secondary on-action-btn me-3 mt-2 mb-2 custom-btn-width">
                    {{ t("w_act_business_request_previous") }}
                </button>

                <button 
                    @click="businessRequestFormStore.loginUser"  
                    type="button"
                    :disabled="!shouldEnableSubmitButton"
                    :class="{ 'invalid-btn-style': !shouldEnableSubmitButton}" 
                    class="btn btn-primary mt-2 mb-2 on-action-btn custom-btn-width">
                    <span v-if="!login_loading">{{ t("w_act_business_request_post") }}</span>
                    <span v-else class="spinner-border spinner-border-sm text-light" role="status">
                      <span class="sr-only">Loading...</span>
                    </span>
                </button>
            </div>
        </div>

    </div>
</template>

<style scoped>
.btn-primary:focus{
    background-color: #FEB23C !important;
}
.custom-input-label {
    text-align: left;
    justify-content: flex-start;
}
.main {
    padding-top: 15px;
    position: relative;
}

.bg {
    background-color: #FEB23C !important;
}

.fullHeight {
    height: 100%;
    z-index: 1;
    border: none !important;
    display: flex;                  
    flex-direction: column;
}

.fullRemainingHeight {
    display: flex;                  
    flex-direction: column; 
    height: 100%;  
}

.expanded{
    flex-grow: 1;
    margin-left: 10px;
    margin-right: 10px;
}

.next-btn {
    background-color: #FEB23C !important;
    padding-top: 10px;
    padding-bottom: 10px;
    border-radius: 10px;
    color: white !important;
    width: 200px;
    border: none;
    margin-top: auto; 
    font-size: 18px;  
}

.back-btn {
    background-color: white !important;
    padding-top: 10px;
    padding-bottom: 10px;
    border-radius: 10px;
    width: 200px;
    border: none;
    margin-top: auto; 
    font-size: 18px;  
    color: #555; 
    border: 2px solid;
}

.custom-btn-width {
    min-width: 130px;
}

/* Medium devices (md) - 768px and up */
@media (min-width: 768px) {
    /* Styles for medium screens */
    .expanded{
        margin-left: 50px !important;
        margin-right: 50px !important;
    }

    .custom-btn-width {
        min-width: 150px !important;
    }
}

.signUpLink {
  color: #FEB23C !important;
  text-decoration: none !important;
}

.invalid {
  background-color: rgb(179, 175, 175) !important;
  border-color: rgb(179, 175, 175) !important;
}
</style>